import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSubscription } from '@/hooks/useSubscription';
import { SubscriptionGate } from '@/components/SubscriptionGate';
import { DashboardLayout } from '@/components/DashboardLayout';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Rocket, Bot, CheckCircle, TrendingUp, PauseCircle, ArrowLeft } from 'lucide-react';
import { QueueManagement } from '@/components/ai-application/QueueManagement';
import { SettingsPanel } from '@/components/ai-application/SettingsPanel';
import { AnalyticsDashboard } from '@/components/ai-application/AnalyticsDashboard';
import { ApplicationHistory } from '@/components/ai-application/ApplicationHistory';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';


export default function AIApplication() {
  const navigate = useNavigate();
  
  const { subscription, loading: subLoading } = useSubscription();
  const [stats, setStats] = useState({
    jobsApplied: 0,
    applicationsThisWeek: 0,
    successRate: 0,
  });
  const [isPaused, setIsPaused] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadStats();
    loadSettings();
  }, []);

  const loadStats = async () => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;

      // Get total applications
      const { count: totalCount } = await supabase
        .from('application_history')
        .select('*', { count: 'exact', head: true })
        .eq('user_id', user.id);

      // Get applications this week
      const weekAgo = new Date();
      weekAgo.setDate(weekAgo.getDate() - 7);
      const { count: weekCount } = await supabase
        .from('application_history')
        .select('*', { count: 'exact', head: true })
        .eq('user_id', user.id)
        .gte('applied_at', weekAgo.toISOString());

      // Calculate success rate (interviews / applications)
      const { count: interviewCount } = await supabase
        .from('application_history')
        .select('*', { count: 'exact', head: true })
        .eq('user_id', user.id)
        .not('interview_at', 'is', null);

      setStats({
        jobsApplied: totalCount || 0,
        applicationsThisWeek: weekCount || 0,
        successRate: totalCount ? Math.round(((interviewCount || 0) / totalCount) * 100) : 0,
      });
    } catch (error) {
      console.error('Error loading stats:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadSettings = async () => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;

      const { data: settings } = await supabase
        .from('auto_apply_settings')
        .select('enabled')
        .eq('user_id', user.id)
        .single();

      if (settings) {
        setIsPaused(!settings.enabled);
      }
    } catch (error) {
      console.error('Error loading settings:', error);
    }
  };

  const handlePauseToggle = async () => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;

      const newState = !isPaused;
      
      const { error } = await supabase
        .from('auto_apply_settings')
        .upsert({
          user_id: user.id,
          enabled: !newState,
        });

      if (error) throw error;

      setIsPaused(newState);
      toast.success(newState ? 'Smart Apply paused' : 'Smart Apply resumed');
    } catch (error) {
      console.error('Error toggling pause:', error);
      toast.error('Failed to update Smart Apply status');
    }
  };

  const scrollToSection = (id: string) => {
    document.getElementById(id)?.scrollIntoView({ behavior: 'smooth' });
  };

  if (subLoading) {
    return <div className="min-h-screen flex items-center justify-center">Loading...</div>;
  }

  return (
    <SubscriptionGate
      requiredPlan="premium"
      feature="AI Smart Apply"
      description="Let AI apply to hundreds of jobs automatically with custom resumes and cover letters for each application."
    >
      <DashboardLayout>
        <div className="p-6 space-y-6">
          {/* Hero Section */}
          <Card className="p-8 bg-gradient-to-br from-primary/10 to-secondary/10">
            <div className="flex flex-col items-center text-center space-y-6">
                <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-primary/10 border border-primary/20">
                  <Bot className="w-4 h-4 text-primary" />
                  <span className="text-sm font-medium text-primary">Premium Feature</span>
                </div>
                
                <h1 className="text-3xl font-bold">
                  Your Personal Job Application Assistant
                </h1>
                
                <p className="text-muted-foreground">
                  AI applies to 100+ jobs while you sleep. Custom resumes for every application.
                </p>

                <div className="flex flex-col sm:flex-row gap-4 mt-4">
                  <Button onClick={() => scrollToSection('queue')}>
                    <Rocket className="mr-2 h-4 w-4" />
                    Start Smart Apply
                  </Button>
                  <Button variant="outline" onClick={() => scrollToSection('how-it-works')}>
                    See How It Works
                  </Button>
                </div>

                {/* Emergency Pause Button */}
                <Button
                  variant={isPaused ? "default" : "destructive"}
                  className="mt-2"
                  onClick={handlePauseToggle}
                >
                <PauseCircle className="mr-2 h-4 w-4" />
                {isPaused ? 'Resume Auto-Applications' : 'Pause All Auto-Applications'}
              </Button>
            </div>
          </Card>
          

          {/* Stats Bar */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <Card className="p-6 bg-card border-border">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">Jobs Applied</p>
                  <p className="text-4xl font-bold text-foreground mt-2">{loading ? '...' : stats.jobsApplied}</p>
                </div>
                <CheckCircle className="h-12 w-12 text-green-500" />
              </div>
            </Card>

            <Card className="p-6 bg-card border-border">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">Applications This Week</p>
                  <p className="text-4xl font-bold text-foreground mt-2">{loading ? '...' : stats.applicationsThisWeek}</p>
                </div>
                <TrendingUp className="h-12 w-12 text-blue-500" />
              </div>
            </Card>

            <Card className="p-6 bg-card border-border">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">Success Rate</p>
                  <p className="text-4xl font-bold text-foreground mt-2">{loading ? '...' : stats.successRate}%</p>
                </div>
                <Bot className="h-12 w-12 text-primary" />
              </div>
            </Card>
          </div>

          {/* Queue Management Section */}
          <div id="queue">
            <QueueManagement onStatsUpdate={loadStats} />
          </div>

          {/* Settings Section */}
          <div id="settings">
            <SettingsPanel />
          </div>

          {/* Analytics Dashboard */}
          <div id="analytics">
            <AnalyticsDashboard />
          </div>

          {/* Application History */}
          <div id="history">
            <ApplicationHistory />
          </div>

          {/* How It Works Section */}
          <div id="how-it-works">
            <h2 className="text-2xl font-bold mb-6">How AI Smart Apply Works</h2>
            
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
              {[
                { icon: '📚', title: 'You Save Jobs', description: 'Save interesting jobs from our job search or upload job links manually' },
                { icon: '🧠', title: 'AI Analyzes Each Job', description: 'Our AI extracts keywords, requirements, and company culture from each job description' },
                { icon: '📄', title: 'Custom Resume Generated', description: 'AI creates a tailored resume highlighting your most relevant experience for each specific job' },
                { icon: '✉️', title: 'Custom Cover Letter Written', description: 'AI writes a personalized cover letter matching the job requirements and company values' },
                { icon: '🛡️', title: 'ATS Optimization', description: 'Resume and cover letter are optimized to pass Applicant Tracking Systems (ATS)' },
                { icon: '🚀', title: 'Auto-Submit Application', description: 'AI automatically submits your application through the job board or company website' },
                { icon: '📊', title: 'Track & Follow-up', description: 'Track all applications in one place and automatically send follow-up emails' },
                { icon: '🔐', title: 'Your Data. Your Control.', description: 'You can pause or stop Smart Apply anytime with full transparency on what was submitted' },
              ].map((step, index) => (
                <Card key={index} className="p-4 text-center hover:shadow-lg transition-shadow">
                  <div className="text-3xl mb-2">{step.icon}</div>
                  <h3 className="text-sm font-semibold mb-1">{step.title}</h3>
                  <p className="text-xs text-muted-foreground">{step.description}</p>
                </Card>
              ))}
            </div>
          </div>
        </div>
      </DashboardLayout>
    </SubscriptionGate>
  );
}
